
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!email || !password) {
      setError('請輸入電子郵件和密碼');
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        setError('登入失敗，請檢查您的電子郵件和密碼');
        setLoading(false);
      } else {
        // 等待角色資訊載入後再導向
        setTimeout(async () => {
          const { createClient } = await import('@supabase/supabase-js');
          const supabase = createClient(
            import.meta.env.VITE_PUBLIC_SUPABASE_URL,
            import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
          );
          
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const { data } = await supabase
              .from('user_profiles')
              .select('role')
              .eq('user_id', user.id)
              .single();
            
            // 根據角色導向不同頁面
            if (data?.role === 'admin') {
              navigate('/admin');
            } else {
              navigate('/profile');
            }
          }
        }, 500);
      }
    } catch (err) {
      setError('登入過程中發生錯誤，請稍後再試');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-3 mb-6">
            <img src="https://public.readdy.ai/ai/img_res/46c4b423-33ec-4c59-b54c-70c60984f453.png" alt="Logo" className="h-12 w-12 object-contain" />
            <span className="text-2xl font-bold text-gray-900">數學學習平台</span>
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">歡迎回來</h1>
          <p className="text-gray-600">登入您的帳號繼續學習</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                電子郵件
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                密碼
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500" />
                <span className="text-sm text-gray-600">記住我</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-orange-500 hover:text-orange-600 cursor-pointer">
                忘記密碼？
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-900 text-white rounded-full font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {loading ? '登入中...' : '登入'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-sm">
              還沒有帳號？{' '}
              <Link to="/register" className="text-orange-500 hover:text-orange-600 font-semibold cursor-pointer">
                立即註冊
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-gray-600 hover:text-gray-900 text-sm cursor-pointer">
            ← 返回首頁
          </Link>
        </div>
      </div>
    </div>
  );
}
